import { useCallback } from "react";
import { useAxiosInstances } from "../axios-instance";

export interface IChatMessage {
  id: string;
  text: string;
  sender: "user" | "bot";
  timestamp: Date;
  suggestions?: string[];
  actions?: Array<{
    type: string;
    label: string;
    value: any;
  }>;
}

export interface IChatResponse {
  response: string;
  conversationId: string;
  suggestions?: string[];
  actions?: Array<{
    type: string;
    label: string;
    value: any;
  }>;
  /** Abrir Configurações na aba help ou faqs; pdfPage para scroll no Manual */
  navigateSuggestion?: { tab: "help" | "faqs"; pdfPage?: number };
  /** Quando true, exibir modal de avaliação do atendimento (ex.: após despedida/obrigado). */
  requestEvaluation?: boolean;
}

export const useChatApi = () => {
  const { axiosAuthInstance } = useAxiosInstances();

  const sendMessage = useCallback(
    async (data: {
      userId: string;
      message: string;
      conversationId?: string;
      context?: {
        currentPage?: string;
        companyId?: string;
        userId?: string;
        userType?: string;
        companyName?: string;
        isResale?: boolean;
      };
    }): Promise<IChatResponse> => {
      try {
        const response = await axiosAuthInstance.post<{ result: IChatResponse }>(
          `/routes/chat/message/${data.userId}`,
          {
            message: data.message,
            conversationId: data.conversationId,
            context: data.context,
          }
        );
        return response.data.result;
      } catch (error: any) {
        if (error?.response?.data?.result) {
          throw {
            message: error.response.data.result.message,
            type: error.response.data.result.type,
          };
        } else {
          throw {
            message: "Erro ao enviar mensagem. Tente novamente.",
            type: "unknown",
          };
        }
      }
    },
    [axiosAuthInstance]
  );

  const submitEvaluation = useCallback(
    async (data: {
      userId: string;
      rating: number;
      conversationId?: string;
      comment?: string;
    }): Promise<void> => {
      await axiosAuthInstance.post(
        `/routes/chat/evaluation/${data.userId}`,
        {
          rating: data.rating,
          conversationId: data.conversationId,
          comment: data.comment,
        }
      );
    },
    [axiosAuthInstance]
  );

  const getSatisfactionReport = useCallback(
    async (params: {
      userId: string;
      companyId?: string; // Now optional
      startDate?: string;
      endDate?: string;
    }): Promise<{
      evaluations: Array<{
        id: string;
        companyId: string;
        companyName: string; // NEW: from backend
        userId: string;
        userName: string; // NEW: from backend
        conversationId?: string;
        rating: number;
        comment?: string;
        createdAt: string;
      }>
    }> => {
      const response = await axiosAuthInstance.get<{ result: { evaluations: any[] } }>(
        "/routes/chat/evaluations/report",
        { params }
      );
      return response.data.result;
    },
    [axiosAuthInstance]
  );

  return {
    sendMessage,
    submitEvaluation,
    getSatisfactionReport,
  };
};